import { z } from 'zod'
import { Model } from '../../_kernel/architecture'
import { AddressRegion } from '../../_kernel/AddressRegion'
import { DateModel } from '../../_kernel/DateModel'
import { PostalCode } from '../../_kernel/PostalCode'
import { Language } from '../../shared/domain/Language'
import { Property } from './Property'
import { PropertyDescription } from './PropertyDescription'
import type { GetV3PropertyResponse } from '../infrastructure/HTTPPropertyRepository/schemas/v3/property/:country/:city/:slug'

export const PropertyDetailsValidation = z.object({
  property: z.instanceof(Property, { message: '[PropertyDetails] property is required' }),
  checkinTime: z
    .instanceof(DateModel, { message: '[PropertyDetails] checkinTime is required' })
    .optional(),
  checkoutTime: z
    .instanceof(DateModel, { message: '[PropertyDetails] checkoutTime is required' })
    .optional(),
  postalCode: z.instanceof(PostalCode, { message: '[PropertyDetails] postalCode is required' }),
  addressRegion: z.instanceof(AddressRegion, {
    message: '[PropertyDetails] addressRegion is required',
  }),
  language: z.instanceof(Language, { message: '[PropertyDetails] language is required' }),
  description: z.instanceof(PropertyDescription, {
    message: '[PropertyDetails] description is required',
  }),
})

export class PropertyDetails extends Model {
  static create({
    property,
    postalCode,
    addressRegion,
    checkinTime,
    checkoutTime,
    language,
    description,
  }: {
    property: Property
    postalCode: PostalCode
    addressRegion: AddressRegion
    checkinTime?: DateModel
    checkoutTime?: DateModel
    language: Language
    description: PropertyDescription
  }) {
    PropertyDetailsValidation.parse({
      property,
      checkinTime,
      checkoutTime,
      postalCode,
      addressRegion,
      language,
      description,
    })
    return new PropertyDetails(
      property,
      postalCode,
      addressRegion,
      language,
      description,
      checkinTime,
      checkoutTime
    )
  }

  static fromPropertyV3API({ json }: { json: GetV3PropertyResponse }) {
    const language = Language.create({ value: json.language })
    const descriptionContent = json.description?.[json.language]

    return PropertyDetails.create({
      property: Property.fromPropertyV3API({ json }),
      postalCode: PostalCode.create({ value: json.postal_code }),
      addressRegion: AddressRegion.create({ value: json.location?.subdivision_1_name ?? '' }),
      checkinTime:
        DateModel.fromDateTimeOrNull(json.conditions?.checkin?.begin_time ?? '') ?? undefined,
      checkoutTime:
        DateModel.fromDateTimeOrNull(json.conditions?.checkout?.time ?? '') ?? undefined,
      language,
      description: descriptionContent
        ? PropertyDescription.create({
            amenities: descriptionContent.amenities,
            businessAmenities: descriptionContent.business_amenities,
            attractions: descriptionContent.attractions,
            location: descriptionContent.location,
            headline: descriptionContent.headline,
            dining: descriptionContent.dining,
            rooms: descriptionContent.rooms,
          })
        : PropertyDescription.null(),
    })
  }

  static fromJSON({ json }: { json: ReturnType<PropertyDetails['toJSON']> }) {
    return PropertyDetails.create({
      property: Property.fromJSON({ json: json.property }),
      postalCode: PostalCode.create({ value: json.postalCode }),
      addressRegion: AddressRegion.create({ value: json.addressRegion }),
      ...(json.checkinTime && { checkinTime: DateModel.create({ value: json.checkinTime }) }),
      ...(json.checkoutTime && { checkoutTime: DateModel.create({ value: json.checkoutTime }) }),
      language: Language.fromJSON(json.language),
      description: json.description
        ? PropertyDescription.fromJSON({ json: json.description })
        : PropertyDescription.null(),
    })
  }

  static fromJSONOrNull({ json }: { json: ReturnType<PropertyDetails['toJSON']> }) {
    try {
      return PropertyDetails.fromJSON({ json })
    } catch {
      return null
    }
  }

  constructor(
    private readonly _property: Property,
    private readonly _postalCode: PostalCode,
    private readonly _addressRegion: AddressRegion,
    private readonly _language: Language,
    private readonly _description: PropertyDescription,
    private readonly _checkinTime?: DateModel,
    private readonly _checkoutTime?: DateModel
  ) {
    super()
  }

  propertyName() {
    return this._property.name
  }

  propertyWithCompany() {
    return `${this.propertyName()} - Stayforlong`
  }

  propertyDescription() {
    return this._description.description()
  }

  propertyImages() {
    return this._property.images
  }

  propertyMainImage() {
    return this._property.mainImage()
  }

  toJSON() {
    return {
      property: this._property.toJSON(),
      checkinTime: this._checkinTime?.value ?? null,
      checkoutTime: this._checkoutTime?.value ?? null,
      postalCode: this._postalCode.getPostalCode(),
      addressRegion: this._addressRegion.getAddressRegion(),
      language: this._language.toJSON(),
      description: this._description.toJSON(),
    }
  }

  getRichHeadJSON(canonicalURL: string) {
    const ldJSON: Record<string, unknown> = {
      '@context': 'https://schema.org',
      '@type': 'Hotel',
    }

    if (this._property.name) ldJSON.name = this._property.name
    if (canonicalURL) ldJSON.url = canonicalURL

    const { latitude, longitude } = this._property.geoCoordinate
    if (latitude && longitude) {
      ldJSON.geo = {
        '@type': 'GeoCoordinates',
        latitude,
        longitude,
      }
    }

    if (this._property.stars) {
      ldJSON.starRating = {
        '@type': 'https://schema.org/Rating',
        ratingValue: this._property.stars,
      }
    }

    if (this._property.rating && this._property.numberOfComments) {
      ldJSON.aggregateRating = {
        '@type': 'AggregateRating',
        ratingValue: this._property.rating,
        reviewCount: this._property.numberOfComments,
        bestRating: 10,
      }
    }

    ldJSON.logo = 'https://static.stayforlong.com/web/images/sfl-logo_x2.png'

    const firstImage = this._property.mainImage()
    if (firstImage) {
      ldJSON.image = `https://${firstImage.bucket}/${firstImage.path}`
    }

    if (this._checkinTime) ldJSON.checkinTime = this._checkinTime.value
    if (this._checkoutTime) ldJSON.checkoutTime = this._checkoutTime.value

    if (this._property._price) {
      ldJSON.priceRange = `${this._property._price.total}€`
    }

    if (
      this._property.address ||
      this._property.city ||
      this._property.country ||
      this._addressRegion.getAddressRegion() ||
      this._postalCode.getPostalCode()
    ) {
      const address: Record<string, string> = {
        '@type': 'PostalAddress',
      }
      if (this._property.address) address.streetAddress = this._property.address
      if (this._property.city) address.addressLocality = this._property.city
      if (this._addressRegion.getAddressRegion())
        address.addressRegion = this._addressRegion.getAddressRegion()
      if (this._postalCode.getPostalCode()) address.postalCode = this._postalCode.getPostalCode()
      if (this._property.country) address.addressCountry = this._property.country
      ldJSON.address = address
    }

    if (this._property._facilities && this._property._facilities.list.length > 0) {
      ldJSON.amenityFeature = this._property._facilities.list.map(facility => ({
        '@type': 'LocationFeatureSpecification',
        name: facility.id,
        value: 'True',
      }))
    }

    return ldJSON
  }
}
